import { useEffect, useState, useRef, useMemo } from "react";

import { useLeadContext } from "../../contexts/LeadContext";
import { getInitials } from "../../utils/getInitials";
import { sortAccountsByName } from "../../utils/accountOptions";

import stylesFindFrame from "../../styles/components/FindFrame.module.css";
import styles from "../../styles/components/LeadsToolbar.module.css";

import { ReactComponent as SearchIcon } from "../../assets/icons/glass.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/add.svg";
import { ReactComponent as DownIcon } from "../../assets/icons/down.svg";
import { ReactComponent as ResetIcon } from "../../assets/icons/reset.svg";

import SearchInput from "../../ui/inputs/SearchInput.jsx";
import Button from "../../ui/buttons/Button.jsx";
import Avatar from "../../ui/avatar/Avatar";

const MAX_VISIBLE_ACCOUNTS = 5;

const LeadsToolbar = ({
  onFilterChange,
  leads,
  onNewLead,
  onOpenLead,
  selectedAccountId = null,
  searchWidth = "100%",
  searchHeight = "2.5rem",
}) => {
  const { accounts: unsortedAccounts, types } = useLeadContext();
  const accounts = useMemo(
    () => sortAccountsByName(unsortedAccounts),
    [unsortedAccounts],
  );

  const [inputValue, setInputValue] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [visibility, setVisibility] = useState("visible");
  const [selectedType, setSelectedType] = useState(null);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isAccountsDropdownOpen, setIsAccountsDropdownOpen] = useState(false);
  const [accountSearchValue, setAccountSearchValue] = useState("");

  const findFrameRef = useRef(null);
  const searchAreaRef = useRef(null);
  const accountsAreaRef = useRef(null);

  const filteredLeads = useMemo(() => {
    const q = (inputValue || "").toLowerCase();

    return leads
      .filter(
        (lead) =>
          (lead.lead_name || "").toLowerCase().includes(q) ||
          (lead.first_name || "").toLowerCase().includes(q) ||
          (lead.last_name || "").toLowerCase().includes(q),
      )
      .slice(0, 3);
  }, [leads, inputValue]);

  const mailAccountOptions = useMemo(() => {
    const byId = new Map();

    (leads || []).forEach((lead) => {
      const responsible = lead.responsible;
      if (!responsible?.id || byId.has(responsible.id)) return;
      byId.set(responsible.id, {
        id: responsible.id,
        name: responsible.name || responsible.email || "Sin casilla",
      });
    });

    return Array.from(byId.values());
  }, [leads]);

  const visibleAccounts = useMemo(() => {
    const firstAccounts = accounts.slice(0, MAX_VISIBLE_ACCOUNTS);

    if (
      selectedAccountId &&
      !firstAccounts.some((account) => account.id === selectedAccountId)
    ) {
      const selectedAccount = accounts.find(
        (account) => account.id === selectedAccountId,
      );
      if (selectedAccount) {
        return [
          ...firstAccounts.slice(0, MAX_VISIBLE_ACCOUNTS - 1),
          selectedAccount,
        ];
      }
    }

    return firstAccounts;
  }, [accounts, selectedAccountId]);

  const hiddenAccountsCount = Math.max(
    accounts.length - MAX_VISIBLE_ACCOUNTS,
    0,
  );

  const filteredAccountOptions = useMemo(() => {
    const query = accountSearchValue.trim().toLowerCase();
    if (!query) return accounts;
    return accounts.filter((account) =>
      (account.name || "").toLowerCase().includes(query),
    );
  }, [accounts, accountSearchValue]);

  const handleAccountClick = (accountId) => {
    const newSelected = accountId === selectedAccountId ? null : accountId;
    onFilterChange("account", newSelected ? [newSelected] : []);
  };

  const closeAccountsDropdown = () => {
    setIsAccountsDropdownOpen(false);
    setAccountSearchValue("");
  };

  const handleAccountsDropdownSelect = (accountId) => {
    handleAccountClick(accountId);
    closeAccountsDropdown();
  };

  const handleSearchSubmit = () => {
    setIsSearching(true);

    onFilterChange("custom", {
      inputValue,
      selectedType,
      selectedUser,
      visibility,
    });

    setInputValue("");
    setIsSearching(false);
    setIsSearchOpen(false);
  };

  const handleSuggestionClick = (lead) => {
    onOpenLead?.(lead);
    setInputValue("");
    setIsSearchOpen(false);
  };

  const handleResetSearch = () => {
    setInputValue("");
    setSelectedType(null);
    setSelectedUser(null);
    setVisibility("visible");
    setIsDropdownOpen(false);
    setIsTypeDropdownOpen(false);
  };

  const UserSelect = () => (
    <div className={stylesFindFrame.inputdropdownUser}>
      <div className={stylesFindFrame.byVisibility}>By Responsible</div>

      <div
        className={stylesFindFrame.userParent}
        onClick={() => setIsDropdownOpen((prev) => !prev)}
        style={{ cursor: "pointer" }}
      >
        <div className={stylesFindFrame.user}>
          <Avatar name={selectedUser?.name || "NA"} size={24} />

          <div className={stylesFindFrame.mailaccount}>
            {selectedUser ? selectedUser.name : "Todas las casillas"}
          </div>
        </div>

        <div className={stylesFindFrame.dropdownArrowWrapper}>
          <DownIcon className={stylesFindFrame.dropdownArrow} />
        </div>
      </div>

      {isDropdownOpen && (
        <div className={stylesFindFrame.dropdownMenu}>
          <div
            className={stylesFindFrame.dropdownItem}
            onClick={() => {
              setSelectedUser(null);
              setIsDropdownOpen(false);
            }}
          >
            Todas las casillas
          </div>

          {mailAccountOptions.map((mailAccountOption) => (
            <div
              key={mailAccountOption.id}
              className={stylesFindFrame.dropdownItem}
              onClick={() => {
                setSelectedUser(mailAccountOption);
                setIsDropdownOpen(false);
              }}
            >
              <Avatar name={mailAccountOption.name} size={24} />

              <span>{mailAccountOption.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const TypeSelect = () => (
    <div className={stylesFindFrame.inputdropdownText}>
      <div className={stylesFindFrame.byVisibility}>By Type</div>

      <div
        className={stylesFindFrame.inputCompletedParent}
        onClick={() => setIsTypeDropdownOpen((prev) => !prev)}
        style={{ cursor: "pointer" }}
      >
        <div className={stylesFindFrame.inputCompleted}>
          {selectedType ? selectedType.name : "Todos los tipos"}
        </div>

        <div className={stylesFindFrame.dropdownArrowWrapper}>
          <DownIcon className={stylesFindFrame.dropdownArrow} />
        </div>
      </div>

      {isTypeDropdownOpen && (
        <div className={stylesFindFrame.dropdownMenu}>
          <div
            className={stylesFindFrame.dropdownItem}
            onClick={() => {
              setSelectedType(null);
              setIsTypeDropdownOpen(false);
            }}
          >
            Todos los tipos
          </div>

          {types.map((type) => (
            <div
              key={type.id}
              className={stylesFindFrame.dropdownItem}
              onClick={() => {
                setSelectedType(type);
                setIsTypeDropdownOpen(false);
              }}
            >
              {type.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const FindFrame = () => (
    <div ref={findFrameRef} className={stylesFindFrame.frameParent}>
      {inputValue.trim() !== "" && (
        <div className={stylesFindFrame.tabSearchParent}>
          {filteredLeads.map((lead) => (
            <div
              key={lead.id}
              className={stylesFindFrame.tabSearch}
              role="button"
              tabIndex={0}
              style={{ cursor: "pointer" }}
              onClick={() => handleSuggestionClick(lead)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleSuggestionClick(lead);
                }
              }}
            >
              <div className={stylesFindFrame.iconsglassParent}>
                <div className={stylesFindFrame.iconsglass}>
                  <SearchIcon className={stylesFindFrame.vectorIcon} />
                </div>

                <div className={stylesFindFrame.novaSmartphones}>
                  <span>
                    {lead.lead_name} ({lead.first_name} {lead.last_name})
                  </span>
                  <span className={stylesFindFrame.rtphones}>
                    {lead.description}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={stylesFindFrame.filters}>
        <div className={stylesFindFrame.filtersInner}>
          <div className={stylesFindFrame.byVisibilityParent}>
            <div className={stylesFindFrame.byVisibility}>By Visibility</div>

            <div className={stylesFindFrame.frameGroup}>
              <div
                className={stylesFindFrame.radioButtonParent}
                onClick={() => setVisibility("visible")}
                style={{ cursor: "pointer" }}
              >
                <div className={stylesFindFrame.radioButton}>
                  <div
                    className={`${stylesFindFrame.radioButtonInner} ${
                      visibility === "visible" ? stylesFindFrame.selected : ""
                    }`}
                  />
                </div>
                <div className={stylesFindFrame.visible}>Visible</div>
              </div>

              <div
                className={stylesFindFrame.radioButtonParent}
                onClick={() => setVisibility("not_visible")}
                style={{ cursor: "pointer" }}
              >
                <div className={stylesFindFrame.radioButton}>
                  <div
                    className={`${stylesFindFrame.radioButtonInner} ${
                      visibility === "not_visible"
                        ? stylesFindFrame.selected
                        : ""
                    }`}
                  />
                </div>
                <div className={stylesFindFrame.visible}>No Visible</div>
              </div>
            </div>
          </div>
        </div>

        <UserSelect />
        <TypeSelect />

        <div className={stylesFindFrame.buttonParent}>
          <div
            className={stylesFindFrame.button}
            onClick={handleSearchSubmit}
            style={{
              cursor: isSearching ? "not-allowed" : "pointer",
              opacity: isSearching ? 0.6 : 1,
            }}
          >
            <div className={stylesFindFrame.action}>
              {isSearching ? "Buscando..." : "Search"}
            </div>
          </div>

          <div
            className={stylesFindFrame.iconsresetWrapper}
            onClick={handleResetSearch}
            style={{ cursor: "pointer" }}
          >
            <div className={stylesFindFrame.iconsglass}>
              <ResetIcon className={stylesFindFrame.vectorIcon} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideFindFrame =
        findFrameRef.current && findFrameRef.current.contains(event.target);

      const clickedInsideSearchArea =
        searchAreaRef.current && searchAreaRef.current.contains(event.target);

      if (!clickedInsideFindFrame && !clickedInsideSearchArea) {
        setInputValue("");
        setIsSearchOpen(false);
      }
    };

    if (isSearchOpen || (inputValue || "").trim() !== "") {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [inputValue, isSearchOpen]);

  useEffect(() => {
    if (!isAccountsDropdownOpen) return undefined;

    const handleClickOutsideAccounts = (event) => {
      if (
        accountsAreaRef.current &&
        !accountsAreaRef.current.contains(event.target)
      ) {
        setIsAccountsDropdownOpen(false);
        setAccountSearchValue("");
      }
    };

    document.addEventListener("mousedown", handleClickOutsideAccounts);

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideAccounts);
    };
  }, [isAccountsDropdownOpen]);

  return (
    <div className={styles.frameGroup}>
      <div className={styles.searchBarParent}>
        <div
          ref={searchAreaRef}
          className={styles.searchInputWrapper}
          onClick={() => setIsSearchOpen(true)}
          onFocus={() => setIsSearchOpen(true)}
        >
          <SearchInput
            value={inputValue}
            onChange={setInputValue}
            placeholder="Buscar..."
            width={searchWidth}
            height={searchHeight}
            minChars={0}
            debounceMs={0}
          />
        </div>

        {(isSearchOpen || inputValue.trim() !== "") && <FindFrame />}

        <div className={styles.leadsWrapper} ref={accountsAreaRef}>
          {visibleAccounts.map((account) => {
            const isSelected = selectedAccountId === account.id;
            const classNames = `
              ${styles.clients1}
              ${
                isSelected
                  ? styles.selected
                  : selectedAccountId
                    ? styles.grayscale
                    : ""
              }
            `;

            return (
              <div
                key={account.id}
                className={classNames}
                data-tooltip={account.name}
                onClick={() => handleAccountClick(account.id)}
              >
                {account.photo ? (
                  <img
                    className={styles.imageIcon}
                    alt={account.name}
                    src={account.photo}
                  />
                ) : (
                  <div className={styles.bubble}>
                    {getInitials(account.name)}
                  </div>
                )}
              </div>
            );
          })}

          {hiddenAccountsCount > 0 && (
            <div
              className={styles.wrapper}
              role="button"
              tabIndex={0}
              style={{ cursor: "pointer" }}
              onClick={() =>
                isAccountsDropdownOpen
                  ? closeAccountsDropdown()
                  : setIsAccountsDropdownOpen(true)
              }
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  if (isAccountsDropdownOpen) {
                    closeAccountsDropdown();
                  } else {
                    setIsAccountsDropdownOpen(true);
                  }
                }
              }}
            >
              <div className={styles.div}>+{hiddenAccountsCount}</div>
            </div>
          )}

          {isAccountsDropdownOpen && (
            <div className={styles.accountsDropdown}>
              <input
                className={styles.accountsDropdownSearch}
                placeholder="Buscar cuenta..."
                aria-label="Buscar cuenta"
                value={accountSearchValue}
                onChange={(event) => setAccountSearchValue(event.target.value)}
                autoFocus
              />

              <div className={styles.accountsDropdownList}>
                {filteredAccountOptions.map((account) => (
                  <div
                    key={account.id}
                    role="button"
                    tabIndex={0}
                    className={`${styles.accountsDropdownItem} ${
                      selectedAccountId === account.id
                        ? styles.accountsDropdownItemSelected
                        : ""
                    }`}
                    onClick={() => handleAccountsDropdownSelect(account.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleAccountsDropdownSelect(account.id);
                      }
                    }}
                  >
                    <Avatar src={account.photo} name={account.name} size={24} />
                    <span>{account.name}</span>
                  </div>
                ))}

                {filteredAccountOptions.length === 0 && (
                  <div className={styles.accountsDropdownEmpty}>
                    Sin resultados
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Button
        type="button"
        iconLeft={<AddIcon />}
        onClick={onNewLead}
        width={128}
        height={40}
        style={{
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 8,
          paddingRight: 12,
          background: "var(--surface-accent-hover)",
          color: "var(--text-cta-primary, white)",
          fontSize: 13,
          fontFamily: "DM Sans",
          fontWeight: 500,
          gap: 0,
        }}
      >
        Nueva Lead
      </Button>
    </div>
  );
};

export default LeadsToolbar;
